/**
 * Central module for building, validating, and syncing MCP ServerDetail objects.
 * Implements validation rules from server.schema.json (2025-12-11).
 * Used by all 3 input modes (form, raw JSON, URL) to produce a shared typed object.
 */
import type {
  ServerDetail,
  ServerDetailValidationError,
  ServerDetailValidationResult,
  ServerPackage,
  SseTransport,
  StreamableHttpTransport,
} from './types'

const SERVER_SCHEMA_URL = 'https://static.modelcontextprotocol.io/schemas/2025-12-11/server.schema.json'
const NAME_PATTERN = /^[a-zA-Z0-9.-]+\/[a-zA-Z0-9._-]+$/
const TRANSPORT_URL_PATTERN = /^https?:\/\/[^\s]+$/
const NAME_MIN_LENGTH = 3
const NAME_MAX_LENGTH = 200
const DESCRIPTION_MAX_LENGTH = 100
const TITLE_MAX_LENGTH = 100
const VERSION_MAX_LENGTH = 255

// Keys that are handled by form fields (not advanced JSON)
const FORM_HANDLED_KEYS = new Set([
  'name',
  'title',
  'description',
  'version',
  'websiteUrl',
  'repository',
  'packages',
  'remotes',
  '$schema',
])

// --- Validation ---

export function validateServerDetail(detail: ServerDetail): ServerDetailValidationResult {
  const errors: ServerDetailValidationError[] = []

  // name: required, pattern, length
  if (!detail.name || typeof detail.name !== 'string' || detail.name.trim().length === 0) {
    errors.push({path: 'name', message: 'Name is required.'})
  } else {
    const name = detail.name.trim()
    if (name.length < NAME_MIN_LENGTH || name.length > NAME_MAX_LENGTH) {
      errors.push({path: 'name', message: `Name must be between ${NAME_MIN_LENGTH} and ${NAME_MAX_LENGTH} characters.`})
    } else if (!NAME_PATTERN.test(name)) {
      errors.push({path: 'name', message: 'Name must be in namespace/server-name format (e.g. acme-corp/my-server).'})
    }
  }

  // description: required, max length
  if (!detail.description || typeof detail.description !== 'string' || detail.description.trim().length === 0) {
    errors.push({path: 'description', message: 'Description is required.'})
  } else if (detail.description.trim().length > DESCRIPTION_MAX_LENGTH) {
    errors.push({path: 'description', message: `Description must be ${DESCRIPTION_MAX_LENGTH} characters or fewer.`})
  }

  // title: optional, 1-100 chars if present
  if (detail.title !== undefined && detail.title !== '') {
    if (typeof detail.title !== 'string') {
      errors.push({path: 'title', message: 'Title must be a string.'})
    } else if (detail.title.trim().length === 0) {
      errors.push({path: 'title', message: 'Title must not be empty if provided.'})
    } else if (detail.title.trim().length > TITLE_MAX_LENGTH) {
      errors.push({path: 'title', message: `Title must be ${TITLE_MAX_LENGTH} characters or fewer.`})
    }
  }

  // version: required, max length
  if (!detail.version || typeof detail.version !== 'string' || detail.version.trim().length === 0) {
    errors.push({path: 'version', message: 'Version is required.'})
  } else if (detail.version.trim().length > VERSION_MAX_LENGTH) {
    errors.push({path: 'version', message: `Version must be ${VERSION_MAX_LENGTH} characters or fewer.`})
  }

  // websiteUrl: optional, must be valid URI if present
  if (detail.websiteUrl !== undefined && detail.websiteUrl !== '') {
    if (typeof detail.websiteUrl !== 'string') {
      errors.push({path: 'websiteUrl', message: 'Website URL must be a string.'})
    } else {
      try {
        const parsedUrl = new URL(detail.websiteUrl, window.location.origin)
        if (!/^https?:\/\//.test(detail.websiteUrl) || parsedUrl.hostname.length === 0) {
          errors.push({path: 'websiteUrl', message: 'Website URL must be a valid URL.'})
        }
      } catch {
        errors.push({path: 'websiteUrl', message: 'Website URL must be a valid URL.'})
      }
    }
  }

  // repository: optional, but if present validate required fields
  if (detail.repository !== undefined) {
    validateRepository(detail.repository, errors)
  }

  // packages: optional array, validate each entry
  if (detail.packages !== undefined) {
    if (!Array.isArray(detail.packages)) {
      errors.push({path: 'packages', message: 'Packages must be an array.'})
    } else {
      for (let i = 0; i < detail.packages.length; i++) {
        validatePackage(detail.packages[i], i, errors)
      }
    }
  }

  // remotes: optional array, validate each entry
  if (detail.remotes !== undefined) {
    if (!Array.isArray(detail.remotes)) {
      errors.push({path: 'remotes', message: 'Remotes must be an array.'})
    } else {
      for (let i = 0; i < detail.remotes.length; i++) {
        validateRemote(detail.remotes[i], i, errors)
      }
    }
  }

  return {valid: errors.length === 0, errors}
}

function validateRepository(repo: unknown, errors: ServerDetailValidationError[]): void {
  if (!repo || typeof repo !== 'object') {
    errors.push({path: 'repository', message: 'Repository must be an object.'})
    return
  }
  const r = repo as Record<string, unknown>
  if (typeof r.url !== 'string' || r.url.trim().length === 0) {
    errors.push({path: 'repository.url', message: 'Repository URL is required.'})
  }
  if (typeof r.source !== 'string' || r.source.trim().length === 0) {
    errors.push({path: 'repository.source', message: 'Repository source is required.'})
  }
}

function validatePackage(pkg: unknown, index: number, errors: ServerDetailValidationError[]): void {
  if (!pkg || typeof pkg !== 'object') {
    errors.push({path: `packages[${index}]`, message: 'Package must be an object.'})
    return
  }
  const p = pkg as Record<string, unknown>
  if (typeof p.registryType !== 'string' || p.registryType.trim().length === 0) {
    errors.push({path: `packages[${index}].registryType`, message: 'Registry type is required.'})
  }
  if (typeof p.identifier !== 'string' || p.identifier.trim().length === 0) {
    errors.push({path: `packages[${index}].identifier`, message: 'Identifier is required.'})
  }
  // version is optional per 2025-12-11 schema, but must be non-empty string if present
  if (p.version !== undefined && p.version !== '') {
    if (typeof p.version !== 'string' || p.version.trim().length === 0) {
      errors.push({
        path: `packages[${index}].version`,
        message: 'Package version must be a non-empty string if provided.',
      })
    }
  }
  if (!p.transport || typeof p.transport !== 'object') {
    errors.push({path: `packages[${index}].transport`, message: 'Transport is required.'})
  } else {
    const t = p.transport as Record<string, unknown>
    if (!['stdio', 'streamable-http', 'sse'].includes(t.type as string)) {
      errors.push({
        path: `packages[${index}].transport.type`,
        message: 'Transport type must be stdio, streamable-http, or sse.',
      })
    }
    if (
      (t.type === 'streamable-http' || t.type === 'sse') &&
      (typeof t.url !== 'string' || !TRANSPORT_URL_PATTERN.test(t.url))
    ) {
      errors.push({path: `packages[${index}].transport.url`, message: 'Transport URL must be a valid http(s) URL.'})
    }
  }
}

function validateRemote(remote: unknown, index: number, errors: ServerDetailValidationError[]): void {
  if (!remote || typeof remote !== 'object') {
    errors.push({path: `remotes[${index}]`, message: 'Remote must be an object.'})
    return
  }
  const r = remote as Record<string, unknown>
  if (!['streamable-http', 'sse'].includes(r.type as string)) {
    errors.push({path: `remotes[${index}].type`, message: 'Remote type must be streamable-http or sse.'})
  }
  if (typeof r.url !== 'string' || !TRANSPORT_URL_PATTERN.test(r.url)) {
    errors.push({path: `remotes[${index}].url`, message: 'Remote URL must be a valid http(s) URL.'})
  }
}

// --- Building from Form ---

export function buildServerDetailFromForm(form: HTMLFormElement): ServerDetail {
  const detail: ServerDetail = {
    $schema: SERVER_SCHEMA_URL,
    name: '',
    description: '',
    version: '',
  }

  // Root fields
  const rootInputs = form.querySelectorAll<HTMLInputElement | HTMLTextAreaElement>('[data-private-server-root-field]')
  for (const input of rootInputs) {
    const field = input.getAttribute('data-private-server-root-field')
    if (!field) continue
    const value = input.value.trim()
    if (value.length > 0) {
      ;(detail as Record<string, unknown>)[field] = value
    }
  }

  // Nested fields (repository)
  const nestedInputs = form.querySelectorAll<HTMLInputElement | HTMLSelectElement>('[data-private-server-nested-field]')
  for (const input of nestedInputs) {
    const path = input.getAttribute('data-private-server-nested-field')
    if (!path) continue
    const value = input.value.trim()
    if (value.length === 0) continue

    const parts = path.split('.')
    let obj = detail as Record<string, unknown>
    for (let i = 0; i < parts.length - 1; i++) {
      const key = parts[i] as string
      if (!obj[key] || typeof obj[key] !== 'object') {
        obj[key] = {}
      }
      obj = obj[key] as Record<string, unknown>
    }

    obj[parts[parts.length - 1] as string] = value
  }

  // Packages
  const packageEntries = form.querySelectorAll('[data-private-server-package-entry]')
  if (packageEntries.length > 0) {
    const packages: ServerPackage[] = []
    for (const entry of packageEntries) {
      const pkg = collectPackageFromEntry(entry)
      if (pkg) packages.push(pkg)
    }
    if (packages.length > 0) detail.packages = packages
  }

  // Remotes
  const remoteEntries = form.querySelectorAll('[data-private-server-remote-entry]')
  if (remoteEntries.length > 0) {
    const remotes: Array<StreamableHttpTransport | SseTransport> = []
    for (const entry of remoteEntries) {
      const remote = collectRemoteFromEntry(entry)
      if (remote) remotes.push(remote)
    }
    if (remotes.length > 0) detail.remotes = remotes
  }

  // Advanced JSON merge (only non-form-handled keys — form fields always take precedence)
  const advancedTextarea = form.querySelector<HTMLTextAreaElement>('[data-private-server-advanced-json]')
  if (advancedTextarea && advancedTextarea.value.trim().length > 0) {
    try {
      const advanced = JSON.parse(advancedTextarea.value)
      if (advanced && typeof advanced === 'object' && !Array.isArray(advanced)) {
        const rootFieldKeys = new Set(
          Array.from(rootInputs)
            .map(input => input.getAttribute('data-private-server-root-field'))
            .filter(Boolean),
        )
        for (const [key, value] of Object.entries(advanced)) {
          if (!FORM_HANDLED_KEYS.has(key) && !rootFieldKeys.has(key)) {
            ;(detail as Record<string, unknown>)[key] = value
          }
        }
      }
    } catch {
      // Invalid advanced JSON is ignored during build (caught by validation)
    }
  }

  return detail
}

function collectPackageFromEntry(entry: Element): ServerPackage | null {
  const fields = entry.querySelectorAll<HTMLInputElement | HTMLSelectElement>('[data-private-server-package-field]')
  const raw: Record<string, unknown> = {}

  for (const field of fields) {
    const key = field.getAttribute('data-private-server-package-field')
    if (!key) continue
    const val = field.value.trim()
    if (val.length === 0) continue

    if (key.includes('.')) {
      const [parent, child] = key.split('.') as [string, string]
      if (!raw[parent] || typeof raw[parent] !== 'object') raw[parent] = {}
      ;(raw[parent] as Record<string, unknown>)[child] = val
    } else {
      raw[key] = val
    }
  }

  if (!raw.registryType && !raw.identifier) return null
  return raw as unknown as ServerPackage
}

function collectRemoteFromEntry(entry: Element): StreamableHttpTransport | SseTransport | null {
  const fields = entry.querySelectorAll<HTMLInputElement | HTMLSelectElement>('[data-private-server-remote-field]')
  const raw: Record<string, unknown> = {}

  for (const field of fields) {
    const key = field.getAttribute('data-private-server-remote-field')
    if (!key) continue
    const val = field.value.trim()
    if (val.length > 0) raw[key] = val
  }

  if (!raw.type && !raw.url) return null
  return raw as unknown as StreamableHttpTransport | SseTransport
}

// --- Building from JSON ---

export function buildServerDetailFromJson(jsonValue: string): ServerDetail | null {
  let parsed: unknown
  try {
    parsed = JSON.parse(jsonValue)
  } catch {
    return null
  }

  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null
  return parsed as ServerDetail
}

// --- Applying to Form ---

export function applyServerDetailToForm(form: HTMLFormElement, detail: ServerDetail): void {
  // Root fields
  const rootInputs = form.querySelectorAll<HTMLInputElement | HTMLTextAreaElement>('[data-private-server-root-field]')
  for (const input of rootInputs) {
    const field = input.getAttribute('data-private-server-root-field')
    if (!field) continue
    const value = (detail as Record<string, unknown>)[field]
    input.value = typeof value === 'string' ? value : ''
  }

  // Nested fields (repository)
  const nestedInputs = form.querySelectorAll<HTMLInputElement | HTMLSelectElement>('[data-private-server-nested-field]')
  for (const input of nestedInputs) {
    const path = input.getAttribute('data-private-server-nested-field')
    if (!path) continue

    const parts = path.split('.')
    let val: unknown = detail
    for (const part of parts) {
      if (val && typeof val === 'object') {
        val = (val as Record<string, unknown>)[part]
      } else {
        val = undefined
        break
      }
    }
    input.value = typeof val === 'string' ? val : ''
  }

  // Packages
  populatePackageEntries(form, detail.packages)

  // Remotes
  populateRemoteEntries(form, detail.remotes)

  // Advanced JSON (keys not handled by form)
  const advanced: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(detail)) {
    if (!FORM_HANDLED_KEYS.has(key)) advanced[key] = value
  }
  const advancedTextarea = form.querySelector<HTMLTextAreaElement>('[data-private-server-advanced-json]')
  if (advancedTextarea) {
    advancedTextarea.value = Object.keys(advanced).length > 0 ? JSON.stringify(advanced, null, 2) : ''
  }
}

function populatePackageEntries(form: HTMLFormElement, packages: unknown): void {
  const list = form.querySelector('[data-private-server-packages-list]')
  if (!list) return

  list.textContent = ''
  if (!Array.isArray(packages) || packages.length === 0) return

  const template =
    form.querySelector<HTMLTemplateElement>('[data-private-server-package-template]') ||
    form.ownerDocument.querySelector<HTMLTemplateElement>('[data-private-server-package-template]')
  if (!template) return

  for (const pkg of packages) {
    if (!pkg || typeof pkg !== 'object') continue
    const clone = template.content.cloneNode(true) as DocumentFragment
    list.appendChild(clone)
    const entry = list.lastElementChild
    if (!entry) continue

    const fields = entry.querySelectorAll<HTMLInputElement | HTMLSelectElement>('[data-private-server-package-field]')
    for (const field of fields) {
      const key = field.getAttribute('data-private-server-package-field')
      if (!key) continue
      let value: unknown
      if (key.includes('.')) {
        const [parent, child] = key.split('.') as [string, string]
        value = (pkg as Record<string, unknown>)[parent]
        if (value && typeof value === 'object') {
          value = (value as Record<string, unknown>)[child]
        } else {
          value = undefined
        }
      } else {
        value = (pkg as Record<string, unknown>)[key]
      }
      if (typeof value === 'string') field.value = value
    }

    // Show/hide transport URL based on type
    const transportType = ((pkg as Record<string, unknown>).transport as Record<string, unknown> | undefined)?.type
    const urlContainer = entry.querySelector<HTMLElement>('[data-private-server-package-transport-url]')
    if (urlContainer) urlContainer.hidden = transportType === 'stdio' || !transportType
  }
}

function populateRemoteEntries(form: HTMLFormElement, remotes: unknown): void {
  const list = form.querySelector('[data-private-server-remotes-list]')
  if (!list) return

  list.textContent = ''
  if (!Array.isArray(remotes) || remotes.length === 0) return

  const template =
    form.querySelector<HTMLTemplateElement>('[data-private-server-remote-template]') ||
    form.ownerDocument.querySelector<HTMLTemplateElement>('[data-private-server-remote-template]')
  if (!template) return

  for (const remote of remotes) {
    if (!remote || typeof remote !== 'object') continue
    const clone = template.content.cloneNode(true) as DocumentFragment
    list.appendChild(clone)
    const entry = list.lastElementChild
    if (!entry) continue

    const fields = entry.querySelectorAll<HTMLInputElement | HTMLSelectElement>('[data-private-server-remote-field]')
    for (const field of fields) {
      const key = field.getAttribute('data-private-server-remote-field')
      if (!key) continue
      const value = (remote as Record<string, unknown>)[key]
      if (typeof value === 'string') field.value = value
    }
  }
}

// --- Serialization ---

export function serializeServerDetail(detail: ServerDetail): string {
  return JSON.stringify(detail, null, 2)
}

// --- Utility: Get first validation error message for UI display ---

export function getFirstValidationError(result: ServerDetailValidationResult): string | null {
  if (result.valid || result.errors.length === 0) return null
  const err = result.errors[0]
  if (!err) return null
  // Include path context for nested errors so the user knows which field is problematic
  if (err.path && err.path.includes('.')) {
    return `${err.path}: ${err.message}`
  }
  return err.message
}

export {FORM_HANDLED_KEYS, SERVER_SCHEMA_URL}
